import { useForm } from "react-hook-form";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Heading from "../../../components/Heading";
import { useEffect, useState } from "react";

import { ActionButton } from "../../../components/Forms/ActionButton";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import ImageInput from "../../../components/Forms/ImageInput";
import { useLocation, useNavigate } from "react-router-dom";
import ActiveStatus from "../../../components/Common/ActiveStatus";
import { useCreateUser, useGetUserById } from "../../../queries/user";

const CreateUser = () => {

    const navigate = useNavigate();

    const { state } = useLocation();
    const userId = state?.userId;

    const schema = z.object({
        first_name: z.string().nonempty(),
        middle_name: z.string(),
        last_name: z.string().nonempty(),
        username: z.string().nonempty(),
        displayName: z.string().nonempty(),
        gender: z.string(),
        email: z.string().email().nonempty(),
        // password: z.string().nonempty(),
        mobile: z.string().nonempty(),
        dob: z.string(),
        ageGroup: z.string(),
        linkedIn: z.string(),
        instagram: z.string(),
        facebook: z.string(),
        twitter: z.string(),
        youTube: z.string(),
        website: z.string(),
        country: z.string(),
        state: z.string(),
        city: z.string(),
        address: z.string(),
        pincode: z.string(),
        landmark: z.string(),
    });

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        reset,
        setValue
    } = useForm({
        defaultValues: {},
        // resolver: zodResolver(schema)
    });

    const [active, setActive] = useState(true);
    const [image, setImage] = useState(null);
    const [imageBase64, setImageBase64] = useState(null);

    const { mutateAsync: createUser, isPending: isCreatingUser } = useCreateUser();
    const { data, isLoading } = useGetUserById(userId);
    console.log("User Data:", data);

    const onSubmit = async (data) => {
        if (!imageBase64) {
            alert("Please upload a profile picture.");
            return;
        }
        console.log({
            ...data,
            active: active,
            profilepic: imageBase64
        });
        createUser({
            ...data,
            active: active,
            profilepic: imageBase64
        }, {
            onSuccess: (res) => {
                if (res.error) {
                    toast.error(`Error creating user: ${res.error.message}`);
                    return;
                }
                toast.success('User created successfully!');
                setTimeout(() => {
                    navigate('/user/list/app-users');
                }, 2000);
            },
            onError: (error) => {
                toast.error(`Error creating user: ${error.message}`);
                console.error("Error creating user:", error);
            }
        });
    }

    useEffect(() => {
        setTimeout(() => {
            if (data) {
                setValue("first_name", data.name.firstName || "");
                setValue("middle_name", data.name.middleName || "");
                setValue("last_name", data.name.lastName || "");
                setValue("username", data.username || "");
                setValue("displayName", data.displayName || "");
                setValue("gender", data.gender || "");
                setValue("email", data.email || "");
                // Don't populate password for security
                setValue("mobile", data.mobile || "");
                setValue("dob", data.dob || "");
                setValue("ageGroup", data.ageGroup || "");
                setValue("linkedIn", data.socialLinks.linkedIn || "");
                setValue("instagram", data.socialLinks.instagram || "");
                setValue("facebook", data.socialLinks.facebook || "");
                setValue("twitter", data.socialLinks.twitter || "");
                setValue("youTube", data.socialLinks.youtube || "");
                setValue("website", data.socialLinks.website || "");
                setValue("country", data.location.country || "");
                setValue("state", data.location.state || "");
                setValue("city", data.location.city || "");
                setValue("address", data.location.address || "");
                setValue("pincode", data.location.pincode || "");
                setValue("landmark", data.location.landmark || "");
                setActive(data.active);
                if (data.profilepic) {
                    setImage(data.profilepic);
                }
            }
        }, 200);
    }, [data, isLoading]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="loader">Loading...</div>
            </div>
        );
    }

    return (
        <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-end">
                <ActiveStatus
                    active={active}
                    setActive={setActive}
                    isCreateForm={true}
                    className="absolute top-4 right-4"
                />
            </div>
            <section className="flex flex-col self-stretch mt-0 w-full max-md:max-w-full">
                {/* Section Start: Personal Details */}
                <div className="flex flex-col justify-center px-5 py-9 w-full font-semibold bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                    <div className="flex gap-3 justify-between items-center">
                        <div className="flex flex-col w-full">
                            <div className="flex flex-wrap gap-3 w-full">
                                {/* First Name */}
                                <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">First Name</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter First Name"
                                            {...register("first_name", { required: "First Name is required" })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.first_name ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.first_name && <p className="text-red text-sm">{errors.first_name.message}</p>}
                                </div>
                                {/* Middle Name */}
                                <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Middle Name</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Middle Name"
                                            {...register("middle_name", { required: false })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.middle_name ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.middle_name && <p className="text-red text-sm">{errors.middle_name.message}</p>}
                                </div>
                                {/* Last Name */}
                                <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Last Name</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Last Name"
                                            {...register("last_name", { required: "Last Name is required!" })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.last_name ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.last_name && <p className="text-red text-sm">{errors.last_name.message}</p>}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-3 mt-5">
                                {/* Username */}
                                <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Username</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Username"
                                            {...register("username", { required: "Username is required!" })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.username ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.username && <p className="text-red text-sm">{errors.username.message}</p>}
                                </div>
                                {/* Display Name */}
                                <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Display Name</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Display Name"
                                            {...register("displayName", { required: "Display Name is required!" })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.displayName ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.displayName && <p className="text-red text-sm">{errors.displayName.message}</p>}
                                </div>
                                {/* Gender */}
                                <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Gender</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <select
                                            {...register("gender", { required: "Gender is required!" })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.gender ? "border-red-500" : ""}`}
                                        >
                                            <option value="">Select Gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                    {errors.gender && <p className="text-red text-sm">{errors.gender.message}</p>}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-3 mt-5">
                                {/* Email */}
                                <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Email</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Email"
                                            {...register("email", { required: "Email is required!" })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.email ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.email && <p className="text-red text-sm">{errors.email.message}</p>}
                                </div>
                                {/* Password */}
                                {/* <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                    <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Password</label>
                                    <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                        <input
                                            type="text"
                                            placeholder="Enter Password"
                                            {...register("password", { required: "Password is required!" })}
                                            className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.password ? "border-red-500" : ""}`}
                                        />
                                    </div>
                                    {errors.password && <p className="text-red text-sm">{errors.password.message}</p>}
                                </div> */}
                            </div>
                        </div>
                        <div className="flex flex-col justify-center items-end self-stretch my-auto w-[180px] basis-1/6">
                            <ImageInput
                                image={image}
                                setImage={setImage}
                                text={'Profile Picture'}
                                setImageBase64={setImageBase64}
                            />
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3 mt-5 justify-end items-end">
                        {/* Mobile Number */}
                        <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Mobile Number</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="number"
                                    placeholder="Enter Mobile Number"
                                    {...register("mobile", { required: "Mobile Number is required!" })}
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.mobile ? "border-red-500" : ""}`}
                                />
                            </div>
                            {errors.mobile && <p className="text-red text-sm">{errors.mobile.message}</p>}
                        </div>
                        {/* DOB */}
                        <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">DOB</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="date"
                                    placeholder="Enter Date of Birth"
                                    {...register("dob", { required: false })}
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.dob ? "border-red-500" : ""}`}
                                />
                            </div>
                            {errors.dob && <p className="text-red text-sm">{errors.dob.message}</p>}
                        </div>
                        {/* Age Group */}
                        <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                            <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Age Group</label>
                            <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    {...register("ageGroup", { required: "Age Group is required!" })}
                                    className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.ageGroup ? "border-red-500" : ""}`}
                                >
                                    <option value="">Select Age Group</option>
                                    <option value="kid">Kid</option>
                                    <option value="adult">Adult</option>
                                    <option value="old">Old</option>
                                </select>
                            </div>
                            {errors.ageGroup && <p className="text-red text-sm">{errors.ageGroup.message}</p>}
                        </div>
                    </div>
                </div>
                {/* Social Links */}
                <section className="flex flex-col self-stretch mt-3 font-semibold max-md:max-w-full">
                    <div className="flex flex-col justify-center px-5 bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:max-w-full py-5 pt-8">
                        <h2 className="text-xl text-gray-900">Social Media Details</h2>
                        <div className="flex flex-wrap gap-3 mt-5 w-full">
                            {/* LinkedIn Account Link */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">LinkedIn Account Link</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter LinkedIn Account Link"
                                        {...register("linkedIn")}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.linkedIn ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.linkedIn && <p className="text-red text-sm">{errors.linkedIn.message}</p>}
                            </div>
                            {/* Instagram Account Link */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Instagram Account Link</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter Instagram Account Link"
                                        {...register("instagram")}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.instagram ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.instagram && <p className="text-red text-sm">{errors.instagram.message}</p>}
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-3 mt-5 w-full">
                            {/* Facebook Account Link */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Facebook Account Link</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter Facebook Account Link"
                                        {...register("facebook")}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.facebook ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.facebook && <p className="text-red text-sm">{errors.facebook.message}</p>}
                            </div>
                            {/* Twitter Account Link */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Twitter Account Link</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter Twitter Account Link"
                                        {...register("twitter")}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.twitter ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.twitter && <p className="text-red text-sm">{errors.twitter.message}</p>}
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-3 mt-5 w-full">
                            {/* YouTube Account Link */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">YouTube Account Link</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter YouTube Account Link"
                                        {...register("youTube")}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.youTube ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.youTube && <p className="text-red text-sm">{errors.youTube.message}</p>}
                            </div>
                            {/* Website Link */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Website Link</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter Website Link"
                                        {...register("website")}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.website ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.website && <p className="text-red text-sm">{errors.website.message}</p>}
                            </div>
                        </div>
                    </div>
                </section>
                {/* Edit Profile(Location Details) */}
                <section className="flex flex-col self-stretch mt-3 font-semibold max-md:max-w-full">
                    <div className="flex flex-col justify-center px-5 bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:max-w-full py-5 pt-8">
                        <h2 className="text-xl text-gray-900">Edit Profile</h2>
                        <div className="flex flex-wrap gap-3 mt-5 w-full">
                            {/* Country */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Country</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter Country"
                                        {...register("country")}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.country ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.country && <p className="text-red text-sm">{errors.country.message}</p>}
                            </div>
                            {/* State */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">State</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter State"
                                        {...register("state")}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.state ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.state && <p className="text-red text-sm">{errors.state.message}</p>}
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-3 mt-5 w-full">
                            {/* City */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">City</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter City"
                                        {...register("city")}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.city ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.city && <p className="text-red text-sm">{errors.city.message}</p>}
                            </div>
                            {/* Address */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Address</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter Address"
                                        {...register("address")}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.address ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.address && <p className="text-red text-sm">{errors.address.message}</p>}
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-3 mt-5 w-full">
                            {/* Pincode */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Pincode</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter Pincode"
                                        {...register("pincode")}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.pincode ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.pincode && <p className="text-red text-sm">{errors.pincode.message}</p>}
                            </div>
                            {/* Landmark */}
                            <div className="flex flex-col flex-1 grow shrink-0 basis-0 min-h-[65px] w-full mt-1.5">
                                <label className="gap-1 self-stretch text-base w-full text-gray-900 font-semibold capitalize">Landmark</label>
                                <div className="flex gap-2.5 items-center mt-1 w-full bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                    <input
                                        type="text"
                                        placeholder="Enter Landmark"
                                        {...register("landmark")}
                                        className={`flex-1 shrink self-stretch my-auto basis-0 outline-primary px-4 py-2.5 rounded-md text-base ${errors.landmark ? "border-red-500" : ""}`}
                                    />
                                </div>
                                {errors.landmark && <p className="text-red text-sm">{errors.landmark.message}</p>}
                            </div>
                        </div>
                    </div>
                </section>
                {/* Form Buttons */}
                <div className="flex justify-between items-center mt-10">
                    <ActionButton
                        variant={"reset"}
                        text={"Reset"}
                        handleSubmit={() => {
                            reset();
                            setImage(null);
                            setImageBase64(null);
                            setActive(true);
                        }}
                    />
                    <div className="flex gap-3">
                        <ActionButton
                            variant={"cancel"}
                            text={"Cancel"}
                            handleSubmit={() => {
                                navigate('/user/list/users-ambassadors');
                            }}
                        />
                        <ActionButton
                            variant={"submit"}
                            text={"Create User"}
                            disabled={isCreatingUser}
                        />
                    </div>
                </div>
            </section>
            {/* <section className="flex flex-col self-stretch mt-5 font-semibold max-md:max-w-full">
                <div className="flex flex-col justify-center px-5 bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)] max-md:max-w-full py-5 pt-8">
                    <h2 className="text-xl text-gray-900">User Impact Metrics</h2>
                    <div className="mt-4">
                        <p className="text-base text-gray-700">Total Earned Points: {data?.points}</p>
                        <p className="text-base text-gray-700">Total Challenges Participated: {data?.challengesParticipated.length}</p>
                        <p className="text-base text-gray-700">Total Challenges Proposed: {data?.proposedChallenges?.length}</p>
                        <p className="text-base text-gray-700">Total Events Attended: {data?.challengesCreated?.length}</p>
                        <p className="text-base text-gray-700">Total Badges Attended: {data?.badges?.length}</p>
                        <p className="text-base text-gray-700">Total Blogs Submitted: {data?.blogs?.length}</p>
                        <p className="text-base text-gray-700">Total Carbon Savings(in KG): {data?.carbonKgSaved} KG</p>
                    </div>
                </div>
            </section> */}
            <ToastContainer
                position="top-right"
                autoClose={5000}
            />
        </form>
    );
}

export default CreateUser;